import { useMutationState } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Address, formatUnits, Hex, isAddressEqual } from 'viem';
import { useReadContract } from 'wagmi';
import { abi } from '../../abis/ExchangeGateway';
import { DEFAULT_SUBACCOUNT, ESTIMATED_SEQUENCER_PROCESSING_TIME } from '../../constants';
import { useAsyncSelect } from '../../hooks/useAsyncSelect';
import { useExchangeAddress } from '../../hooks/useDataForSigning';
import {
  getInitiateWithdrawKey,
  useInitiateWithdraw,
  useInitiateWithdrawSignature,
} from '../../hooks/useInitiateWithdraw';
import { useModalState } from '../../hooks/useModalState';
import { usePendingWithdraw } from '../../hooks/usePendingWithdraw';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { SubaccountBalance, useSubaccountBalance } from '../../hooks/useSubaccountBalance';
import { TokenDetails, useTokenDetails } from '../../hooks/useTokenDetails';
import { useTokenPrices } from '../../hooks/useTokenPrices';
import { parseRpcError } from '../../lib/errors';
import {
  formatDollar,
  formatDuration,
  formatNumber,
  formatWrappedToken,
} from '../../lib/formatters';
import { Gwei, GWEI_BASE } from '../../lib/gwei';
import { decodeSubaccountName } from '../../lib/subaccountHelpers';
import { Alert } from '../Alert';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { SelectableTokenInput } from '../SelectableTokenInput/SelectableTokenInput';
import { StyledDialogStat, StyledDialogStats } from '../StyledDialog/StyledDialogStats';
import { SubmitTxButton, SubmitTxStatus } from '../SubmitTxButton/SubmitTxButton';
import { WithdrawModalSkeleton } from './WithdrawModalSkeleton';

const DUST_TO_LEAVE = 100n;

export type WithdrawProps = {
  subaccountName?: Hex;
  isPending: boolean;
  signatureStatus?: SubmitTxStatus;
  error?: Error | null;
  balances?: SubaccountBalance;
  defaultTokenAddress?: Address;
  withdrawLockout?: bigint;
  tokenDetails: TokenDetails[];
  clearError: () => void;
  onWithdraw: (withdraw: { tokenId: string; token: Address; amount: bigint }) => void;
  onClose: () => void;
  onComplete?: () => void;
  amount?: string;
  setAmount: (amount: string) => void;
};

export const WithdrawModalUi: React.FC<WithdrawProps> = ({
  subaccountName,
  isPending,
  signatureStatus,
  error,
  balances,
  defaultTokenAddress,
  withdrawLockout,
  tokenDetails,
  amount,
  setAmount,
  clearError,
  onWithdraw,
  onClose,
  onComplete,
}) => {
  const isOpen = useModalState((state) => state.withdrawalModal === 'open');

  const formattedBalances = balances?.map((balance) => {
    const tokenDetail = tokenDetails?.find((tokenDetail) =>
      isAddressEqual(tokenDetail.address, balance.tokenAddress)
    );

    const balanceMinusDust = {
      ...balance,
      available: Gwei.hasDecimals(balance.available)
        ? balance.available - DUST_TO_LEAVE
        : balance.available,
    };
    return {
      label: formatWrappedToken(tokenDetail?.erc20Symbol) || balance.tokenName,
      value: { ...balanceMinusDust, symbol: tokenDetail?.erc20Symbol },
    };
  });
  const defaultOption =
    formattedBalances?.find((x) => x.value.tokenAddress === defaultTokenAddress)?.value ||
    formattedBalances?.[0]?.value;
  const {
    selectedValue: selectedCollateral,
    setSelectedValue: setSelectedCollateral,
    options,
  } = useAsyncSelect(formattedBalances, defaultOption);

  const selectedTokenDetails = tokenDetails?.find(
    (tokenDetail) =>
      tokenDetail.address.toLowerCase() === selectedCollateral?.value.tokenAddress.toLowerCase()
  );

  const gweiAmount = amount ? Gwei.toGwei(amount) : 0n;
  const gweiAmountAfterFees = gweiAmount - (selectedTokenDetails?.withdrawFee || 0n);

  const erc20Symbol = selectedTokenDetails?.erc20Symbol;
  const tokenPrices = useTokenPrices(erc20Symbol ? [erc20Symbol] : []);

  const validationError = (() => {
    if (signatureStatus === 'complete') return undefined;
    if (selectedCollateral && gweiAmount > selectedCollateral.value.available) {
      return `Insufficient ${formatWrappedToken(selectedTokenDetails?.erc20Symbol) || selectedCollateral.value.tokenName} balance`;
    }

    if (gweiAmount && selectedTokenDetails && gweiAmount <= selectedTokenDetails.withdrawFee) {
      return `Withdraw amount must be greater than withdraw fee.`;
    }

    return;
  })();

  const estimatedTime = withdrawLockout
    ? ESTIMATED_SEQUENCER_PROCESSING_TIME + Number(withdrawLockout)
    : undefined;

  const isDisabled = Boolean(
    validationError || !selectedCollateral || gweiAmount === 0n || isPending
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!gweiAmount || !selectedCollateral) return;
    onWithdraw({
      tokenId: selectedCollateral.value.tokenId,
      token: selectedCollateral.value.tokenAddress,
      amount: gweiAmount,
    });
  };

  return (
    <ResponsiveModal
      onClose={signatureStatus === 'complete' && onComplete ? onComplete : onClose}
      open={isOpen}
      title="Withdraw"
    >
      {!subaccountName ? (
        <WithdrawModalSkeleton />
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          {subaccountName !== DEFAULT_SUBACCOUNT ? (
            <Alert className="text-contentSecondary">
              Withdrawing from{' '}
              <span className="text-contentPrimary">{decodeSubaccountName(subaccountName)}</span>{' '}
              subaccount
            </Alert>
          ) : null}

          <SelectableTokenInput
            amount={amount}
            selectedToken={selectedCollateral}
            options={options}
            tokenPrice={erc20Symbol ? tokenPrices.data[erc20Symbol] : undefined}
            balance={selectedCollateral?.value.available}
            hasError={Boolean(validationError || error)}
            disabled={!!signatureStatus}
            balanceLabel="Available Balance"
            autoFocus={true}
            setAmount={(amount) => {
              if (error) clearError();
              setAmount(amount);
            }}
            setSelectedToken={(token) => {
              if (error) clearError();
              setSelectedCollateral(token);
            }}
            setMax={() =>
              selectedCollateral
                ? setAmount(formatUnits(selectedCollateral.value.available, GWEI_BASE))
                : null
            }
          />

          <StyledDialogStats>
            <StyledDialogStat label="Estimated time:">
              {formatDuration(estimatedTime)}
            </StyledDialogStat>
            <StyledDialogStat label="Fee">
              {selectedTokenDetails?.name === 'USD'
                ? formatDollar(selectedTokenDetails?.withdrawFee)
                : `${formatNumber(selectedTokenDetails?.withdrawFee)} ${selectedTokenDetails?.name ?? ''}`}
            </StyledDialogStat>

            <StyledDialogStat label="Amount after fees:">
              {gweiAmountAfterFees > 0
                ? selectedTokenDetails?.name === 'USD'
                  ? formatDollar(gweiAmountAfterFees)
                  : `${formatNumber(gweiAmountAfterFees)} ${selectedTokenDetails?.name ?? ''}`
                : '-'}
            </StyledDialogStat>
          </StyledDialogStats>

          {signatureStatus === 'complete' ? (
            <Alert variant="success">
              Your withdrawal of {formatNumber(gweiAmountAfterFees)}{' '}
              {formatWrappedToken(selectedTokenDetails?.erc20Symbol) || selectedCollateral?.label}{' '}
              is currently being processed.
            </Alert>
          ) : validationError || error ? (
            <Alert variant="error">{validationError || parseRpcError(error)}</Alert>
          ) : null}

          <SubmitTxButton
            type="submit"
            status={signatureStatus}
            actionText="Withdraw"
            completeText="Continue"
            disabled={isDisabled}
            onComplete={onComplete}
          />
        </form>
      )}
    </ResponsiveModal>
  );
};

export type WithdrawModalProps = {
  onWithdraw?: () => void;
  defaultTokenAddress?: Address;
};

export const WithdrawModal: React.FC<WithdrawModalProps> = ({
  defaultTokenAddress,
  onWithdraw,
}) => {
  const [amount, setAmount] = useState<string | undefined>();
  const selectedSubaccount = useActiveSelectedSubaccount();
  const { data: pendingWithdraws } = usePendingWithdraw(selectedSubaccount?.id);
  const withdrawalModalOpen = useModalState((state) => state.withdrawalModal === 'open');
  const setWithdrawalModal = useModalState((state) => state.setWithdrawalModal);
  const { data: subaccountBalance } = useSubaccountBalance(selectedSubaccount?.id);
  const { address: exchangeAddress } = useExchangeAddress();
  const { data: withdrawLockout } = useReadContract({
    abi,
    address: exchangeAddress,
    functionName: 'getExchangeWithdrawLockout',
  });
  const { data: tokenDetails } = useTokenDetails();
  const withdrawSignatureMutation = useInitiateWithdrawSignature();
  const initiateWithdrawMutation = useInitiateWithdraw();
  // Track withdrawal status globally via useMutationState since the mutation
  // status won't update if another component resets the cache.
  // Flow: initiate → WebSocket invalidates → poll until readyAt → reset mutation
  const initiateWithdrawState = useMutationState({
    filters: { mutationKey: getInitiateWithdrawKey(selectedSubaccount?.id) },
  }).at(0);
  useEffect(() => {
    if (initiateWithdrawState?.status !== 'success' && !withdrawalModalOpen) {
      setAmount(undefined);
    }
  }, [initiateWithdrawState?.status, withdrawalModalOpen]);
  const handleClose = () => {
    setWithdrawalModal('closed');
  };

  const handleComplete = () => {
    if (onWithdraw) {
      onWithdraw();
    }
    handleClose();
  };

  const handleWithdraw = ({
    tokenId,
    token,
    amount,
  }: {
    tokenId: string;
    token: Address;
    amount: bigint;
  }) => {
    withdrawSignatureMutation.mutate(
      { tokenId, token, amount },
      {
        onSuccess: ({ signature, message }) => {
          initiateWithdrawMutation.mutate({ signature, message, tokenId });
        },
      }
    );
  };
  const signatureStatus = (() => {
    if (initiateWithdrawState?.status === 'pending') {
      return 'submitting';
    }
    if (initiateWithdrawState?.status === 'success' || pendingWithdraws?.length) {
      return 'complete';
    }
    if (withdrawSignatureMutation.status === 'pending') {
      return 'waiting-signature';
    }
    return undefined;
  })();

  return (
    <WithdrawModalUi
      subaccountName={selectedSubaccount?.name}
      balances={subaccountBalance}
      defaultTokenAddress={defaultTokenAddress}
      isPending={initiateWithdrawMutation.isPending}
      signatureStatus={signatureStatus}
      error={initiateWithdrawMutation.error}
      withdrawLockout={withdrawLockout ? withdrawLockout * 1000n : undefined}
      tokenDetails={tokenDetails ?? []}
      amount={amount}
      setAmount={setAmount}
      clearError={initiateWithdrawMutation.reset}
      onWithdraw={handleWithdraw}
      onClose={handleClose}
      onComplete={handleComplete}
    />
  );
};
